import type { DeviceType, DiagramEdge, DiagramNode, PortLayout } from './types.js';
import { DIAGRAM_GRID_SIZE, ceilToGrid, snapCoord } from './diagramGrid.js';
import {
  buildPortsFromInterfaces,
  interfacesWiredCount,
  resolveDeviceInterfaces,
} from './deviceInterfaces.js';
import {
  buildPortsFromProfile,
  profileWiredCount,
  resolvePortProfile,
  type PortRole,
} from './portProfiles.js';

export type PortSide = 'left' | 'right' | 'top' | 'bottom';

export type DevicePortKind = 'ethernet' | 'wifi';

export type DevicePort = {
  /** Stable handle / edge id, e.g. Eth-1, WAN, SFP-1, WiFi-1 */
  id: string;
  /** UI label */
  label: string;
  index: number;
  side: PortSide;
  /** 0..1 position along the side (for CSS %) */
  t: number;
  kind: DevicePortKind;
  /** Wired role when built from a port profile */
  role?: PortRole;
};

/** Device types that may expose Ethernet-style ports on the diagram. */
export const PORT_DEVICE_TYPES: readonly DeviceType[] = [
  'switch',
  'router',
  'wifi_router',
  'access_point',
  'firewall',
  'modem',
  'server',
  'nas',
  'desktop',
  'laptop',
  'ip_phone',
  'wifi_phone',
  'printer',
  'camera',
  'display',
  'controller',
  'iot',
  'tablet',
  'lte_antenna',
  'satellite',
  'ups',
  'power_station',
  'generic',
] as const;

/** Device types that may expose Wi‑Fi antenna connectors. */
export const WIFI_ANTENNA_DEVICE_TYPES: readonly DeviceType[] = [
  'wifi_router',
  'access_point',
  'router',
  'wifi_phone',
  'ip_phone',
  'tablet',
  'laptop',
  'iot',
  'controller',
  'modem',
  'satellite',
  'ups',
  'power_station',
  'generic',
] as const;

const PORT_TYPE_SET = new Set<DeviceType>(PORT_DEVICE_TYPES);
const WIFI_TYPE_SET = new Set<DeviceType>(WIFI_ANTENNA_DEVICE_TYPES);

export function deviceSupportsPorts(type: DeviceType): boolean {
  return PORT_TYPE_SET.has(type);
}

export function deviceSupportsWifiAntennas(type: DeviceType): boolean {
  return WIFI_TYPE_SET.has(type);
}

/** Sensible Eth defaults by role (0 = no Eth on typical model). */
export function defaultPortCount(type: DeviceType): number {
  switch (type) {
    case 'switch':
      return 8;
    case 'router':
    case 'wifi_router':
      return 5; // 1 WAN + 4 LAN
    case 'access_point':
      return 1; // typical PoE uplink
    case 'firewall':
      return 4;
    case 'modem':
      return 2;
    case 'server':
    case 'nas':
      return 2;
    case 'desktop':
      return 1;
    case 'laptop':
    case 'ip_phone':
    case 'printer':
    case 'camera':
    case 'display':
    case 'controller':
    case 'lte_antenna':
    case 'satellite':
    case 'ups':
    case 'power_station':
    case 'generic':
      return 1;
    case 'wifi_phone':
    case 'tablet':
    case 'iot':
      return 0; // often Wi‑Fi only
    default:
      return 0;
  }
}

export function defaultWifiAntennaCount(type: DeviceType): number {
  switch (type) {
    case 'wifi_router':
    case 'access_point':
      return 2;
    case 'wifi_phone':
    case 'tablet':
      return 1;
    case 'ip_phone':
    case 'laptop':
    case 'iot':
    case 'controller':
    case 'satellite':
    case 'ups':
    case 'power_station':
      return 0;
    default:
      return 0;
  }
}

export function defaultPortLayout(type: DeviceType): PortLayout {
  switch (type) {
    case 'switch':
      return 'both';
    case 'router':
    case 'wifi_router':
    case 'firewall':
      return 'both';
    default:
      return 'both';
  }
}

/** Quick picks for the edit form. */
export const PORT_COUNT_PRESETS: Partial<Record<DeviceType, number[]>> = {
  switch: [5, 8, 16, 24, 48],
  router: [0, 1, 2, 4, 5, 8, 10],
  wifi_router: [0, 1, 2, 4, 5, 8, 10],
  access_point: [0, 1, 2],
  firewall: [2, 4, 5, 8],
  modem: [0, 1, 2, 4],
  server: [1, 2, 4],
  nas: [1, 2, 4],
  desktop: [0, 1, 2],
  laptop: [0, 1],
  ip_phone: [1, 2],
  wifi_phone: [0, 1],
  tablet: [0, 1],
  printer: [0, 1],
  camera: [0, 1],
  display: [0, 1],
  controller: [0, 1, 2],
  iot: [0, 1],
  lte_antenna: [0, 1, 2],
  satellite: [0, 1, 2],
  ups: [0, 1, 2],
  power_station: [0, 1, 2],
  generic: [0, 1, 2, 4],
};

export const WIFI_ANTENNA_PRESETS = [0, 1, 2, 3, 4, 6, 8] as const;

export const PORT_LAYOUT_OPTIONS: readonly { id: PortLayout; labelUk: string }[] = [
  { id: 'left', labelUk: 'Лише зліва' },
  { id: 'right', labelUk: 'Лише справа' },
  { id: 'both', labelUk: 'Зліва і справа' },
  { id: 'all', labelUk: 'Усі сторони' },
] as const;

export function resolvePortCount(
  node: Pick<DiagramNode, 'type' | 'portCount' | 'portProfileId' | 'interfaces'>,
): number {
  if (!deviceSupportsPorts(node.type)) return 0;
  if (node.interfaces) {
    return interfacesWiredCount(resolveDeviceInterfaces(node));
  }
  const profile = resolvePortProfile(node);
  const useProfile = Boolean(
    profile && (node.portProfileId || (node.portCount == null && defaultUsesProfile(node.type))),
  );
  if (useProfile && profile) return profileWiredCount(profile);
  const n = node.portCount;
  if (typeof n === 'number' && Number.isFinite(n) && n >= 0) {
    return Math.min(64, Math.max(0, Math.round(n)));
  }
  return defaultPortCount(node.type);
}

function defaultUsesProfile(type: DeviceType): boolean {
  return (
    type === 'router' ||
    type === 'wifi_router' ||
    type === 'access_point' ||
    type === 'lte_antenna' ||
    type === 'firewall' ||
    type === 'modem' ||
    type === 'switch'
  );
}

export function resolveWifiAntennaCount(
  node: Pick<DiagramNode, 'type' | 'wifiAntennaCount' | 'interfaces'>,
): number {
  if (!deviceSupportsWifiAntennas(node.type)) return 0;
  if (node.interfaces) {
    const iface = resolveDeviceInterfaces(node);
    if (!iface.hasWifi) return 0;
    return Math.min(16, Math.max(0, iface.wifiCount ?? 0));
  }
  const n = node.wifiAntennaCount;
  if (typeof n === 'number' && Number.isFinite(n) && n >= 0) {
    return Math.min(16, Math.max(0, Math.round(n)));
  }
  return defaultWifiAntennaCount(node.type);
}

export function resolvePortLayout(
  node: Pick<DiagramNode, 'type' | 'portLayout'>,
): PortLayout {
  const layout = node.portLayout;
  if (layout === 'left' || layout === 'right' || layout === 'both' || layout === 'all') {
    return layout;
  }
  return defaultPortLayout(node.type);
}

export function portIdForIndex(index: number): string {
  return `Eth-${index}`;
}

export function wifiAntennaIdForIndex(index: number): string {
  return `WiFi-${index}`;
}

/** Logical / unspecified ethernet attachment (shown on edges instead of «?»). */
export const ETHERNET_PORT_ID = '[ethernet]';

export function displayPortId(port?: string | null): string {
  const p = port?.trim();
  if (!p || p === '?' || p === 'unknown') return ETHERNET_PORT_ID;
  return p;
}

export function isLogicalEthernetPort(port?: string | null): boolean {
  const p = displayPortId(port);
  return p === ETHERNET_PORT_ID;
}

export function isWifiPortId(port?: string | null): boolean {
  const p = port?.trim() ?? '';
  return /^WiFi-\d+$/i.test(p);
}

export function portLabelForIndex(type: DeviceType, index: number, total: number): string {
  const isRouterLike =
    type === 'router' || type === 'wifi_router' || type === 'firewall' || type === 'modem';
  if (isRouterLike && total >= 2) {
    if (index === 1) return 'WAN';
    return `LAN-${index - 1}`;
  }
  return `Eth-${index}`;
}

/**
 * Assign sides counter-clockwise starting at top-left.
 * - left: top → bottom
 * - bottom: left → right
 * - right: bottom → top
 * - top: right → left
 */
export function assignPortSides(count: number, layout: PortLayout = 'both'): PortSide[] {
  if (count <= 0) return [];

  if (layout === 'left') return Array.from({ length: count }, () => 'left' as const);
  if (layout === 'right') return Array.from({ length: count }, () => 'right' as const);

  if (layout === 'both') {
    const left = Math.ceil(count / 2);
    return [
      ...Array.from({ length: left }, () => 'left' as const),
      ...Array.from({ length: count - left }, () => 'right' as const),
    ];
  }

  // all four sides, CCW order of filling: L → B → R → T
  const order: PortSide[] = ['left', 'bottom', 'right', 'top'];
  const base = Math.floor(count / 4);
  const rem = count % 4;
  const out: PortSide[] = [];
  for (let i = 0; i < 4; i++) {
    const n = base + (i < rem ? 1 : 0);
    for (let j = 0; j < n; j++) out.push(order[i]!);
  }
  return out;
}

/** Position along side following CCW direction for that edge. */
function tAlongSide(side: PortSide, idxOnSide: number, totalOnSide: number): number {
  if (totalOnSide <= 1) return 0.5;
  const sequential = idxOnSide / (totalOnSide + 1); // 1..n → toward "start" of CSS axis
  // CSS: left/right use top%; top/bottom use left%
  // CCW: left top→bottom = increasing top ✓
  //      bottom left→right = increasing left ✓
  //      right bottom→top = decreasing top
  //      top right→left = decreasing left
  if (side === 'right' || side === 'top') {
    return 1 - sequential;
  }
  return sequential;
}

export function buildDevicePorts(
  type: DeviceType,
  portCount: number,
  layout: PortLayout = 'both',
): DevicePort[] {
  const count = Math.min(64, Math.max(0, Math.round(portCount)));
  const sides = assignPortSides(count, layout);
  const perSide = { left: 0, right: 0, top: 0, bottom: 0 };
  const sideTotals = { left: 0, right: 0, top: 0, bottom: 0 };
  for (const s of sides) sideTotals[s] += 1;

  const ports: DevicePort[] = [];
  for (let i = 0; i < count; i++) {
    const side = sides[i]!;
    perSide[side] += 1;
    const idxOnSide = perSide[side];
    const totalOnSide = sideTotals[side];
    const index = i + 1;
    ports.push({
      id: portIdForIndex(index),
      label: portLabelForIndex(type, index, count),
      index,
      side,
      t: tAlongSide(side, idxOnSide, totalOnSide),
      kind: 'ethernet',
    });
  }
  return ports;
}

/** Wi‑Fi antennas sit on the top edge (left → right). */
export function buildWifiAntennas(count: number): DevicePort[] {
  const n = Math.min(16, Math.max(0, Math.round(count)));
  const ports: DevicePort[] = [];
  for (let i = 0; i < n; i++) {
    const index = i + 1;
    const t = n <= 1 ? 0.5 : (i + 1) / (n + 1);
    ports.push({
      id: wifiAntennaIdForIndex(index),
      label: `WiFi-${index}`,
      index,
      side: 'top',
      t,
      kind: 'wifi',
    });
  }
  return ports;
}

export function listNodePorts(
  node: Pick<
    DiagramNode,
    'type' | 'portCount' | 'portLayout' | 'portProfileId' | 'wifiAntennaCount' | 'interfaces'
  >,
): DevicePort[] {
  const layout = resolvePortLayout(node);
  if (node.interfaces) {
    const iface = resolveDeviceInterfaces(node);
    const eth = deviceSupportsPorts(node.type)
      ? buildPortsFromInterfaces(iface, layout)
      : [];
    const wifi = deviceSupportsWifiAntennas(node.type)
      ? buildWifiAntennas(iface.hasWifi ? iface.wifiCount ?? 0 : 0)
      : [];
    return [...eth, ...wifi];
  }
  const profile = resolvePortProfile(node);
  const useProfile = Boolean(
    profile && (node.portProfileId || (node.portCount == null && defaultUsesProfile(node.type))),
  );
  const eth =
    useProfile && profile
      ? buildPortsFromProfile(profile, layout)
      : buildDevicePorts(node.type, resolvePortCount(node), layout);
  const wifi = buildWifiAntennas(resolveWifiAntennaCount(node));
  return [...eth, ...wifi];
}

export function buildOccupiedPortMap(
  edges: readonly DiagramEdge[],
  exceptEdgeId?: string,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const add = (nodeId: string, port?: string) => {
    if (!port) return;
    let set = map.get(nodeId);
    if (!set) {
      set = new Set();
      map.set(nodeId, set);
    }
    set.add(port);
  };
  for (const e of edges) {
    if (exceptEdgeId && e.id === exceptEdgeId) continue;
    add(e.sourceNodeId, e.sourcePort);
    add(e.targetNodeId, e.targetPort);
  }
  return map;
}

export function occupiedPortsForNode(
  edges: readonly DiagramEdge[],
  nodeId: string,
  exceptEdgeId?: string,
): Set<string> {
  return buildOccupiedPortMap(edges, exceptEdgeId).get(nodeId) ?? new Set();
}

export function firstFreePortId(
  type: DeviceType,
  portCount: number,
  occupied: Set<string>,
  layout: PortLayout = 'both',
): string | undefined {
  const ports = buildDevicePorts(type, portCount, layout);
  return ports.find((p) => !occupied.has(p.id))?.id;
}

export function firstFreeAttachmentId(
  node: Pick<
    DiagramNode,
    'type' | 'portCount' | 'portLayout' | 'wifiAntennaCount' | 'portProfileId' | 'interfaces'
  >,
  occupied: Set<string>,
): string | undefined {
  return listNodePorts(node).find((p) => !occupied.has(p.id))?.id;
}

/** Minimum readable device block on the canvas (grid-aligned). */
export const DEVICE_NODE_MIN_WIDTH = 160;
export const DEVICE_NODE_MIN_HEIGHT = 144;

/**
 * Port position along a side as CSS %, snapped so the handle sits on the diagram grid
 * when the node origin and side length are grid-aligned.
 */
export function gridAlignedPortPercent(
  sideLengthPx: number,
  t: number,
  grid: number = DIAGRAM_GRID_SIZE,
): number {
  const len = Math.max(grid, sideLengthPx);
  const raw = t * len;
  const snapped = Math.min(len - grid / 2, Math.max(grid / 2, snapCoord(raw, grid)));
  return (snapped / len) * 100;
}

/** Grow node box so port rows + label + antennas fit (sizes on grid). */
export function suggestSizeForPorts(
  portCount: number,
  current?: { width: number; height: number },
  layout: PortLayout = 'both',
  wifiAntennaCount = 0,
): { width: number; height: number } {
  const sides = assignPortSides(portCount, layout);
  const sideCount = {
    left: sides.filter((s) => s === 'left').length,
    right: sides.filter((s) => s === 'right').length,
    top: sides.filter((s) => s === 'top').length + Math.max(0, wifiAntennaCount),
    bottom: sides.filter((s) => s === 'bottom').length,
  };
  const maxLR = Math.max(sideCount.left, sideCount.right, 1);
  const maxTB = Math.max(sideCount.top, sideCount.bottom, 1);
  const width = ceilToGrid(
    Math.max(
      DEVICE_NODE_MIN_WIDTH,
      DEVICE_NODE_MIN_WIDTH - 28 + maxTB * 12,
      current?.width ?? 0,
    ),
  );
  const height = ceilToGrid(
    Math.max(
      DEVICE_NODE_MIN_HEIGHT,
      DEVICE_NODE_MIN_HEIGHT - 24 + maxLR * 11,
      current?.height ?? 0,
    ),
  );
  return { width, height };
}
